import { useId, useRef, useState } from "react";

/**
 * Inline definition for assessment terms.
 * Dashed brand underline + glassmorphism tooltip with fade-in.
 */
export default function TermTooltip({ phrase, definition, children, onAskInGuide }) {
  const [open, setOpen] = useState(false);
  const tipId = useId();
  const closeTimer = useRef(null);

  if (!phrase) return children;

  const show = () => {
    clearTimeout(closeTimer.current);
    setOpen(true);
  };
  const hide = () => {
    closeTimer.current = setTimeout(() => setOpen(false), 120);
  };

  return (
    <>
      {/* inject tooltip animation once */}
      <style>{`
        @keyframes term-tip-in {
          from { opacity:0; transform:translateY(4px) scale(0.97); }
          to   { opacity:1; transform:translateY(0)  scale(1); }
        }
        .term-tip-enter {
          animation: term-tip-in 0.15s cubic-bezier(0.22,1,0.36,1) both;
        }
      `}</style>

      <span className="relative inline">
        <button
          type="button"
          aria-describedby={open ? tipId : undefined}
          onMouseEnter={show}
          onMouseLeave={hide}
          onFocus={show}
          onBlur={hide}
          onClick={(e) => {
            e.preventDefault();
            if (onAskInGuide && definition) {
              onAskInGuide(phrase);
              return;
            }
            setOpen((v) => !v);
          }}
          className="term-tooltip-trigger"
          style={{
            textDecoration: "underline",
            textDecorationStyle: "dashed",
            textDecorationThickness: 2,
            textUnderlineOffset: 4,
            fontWeight: 600,
            cursor: "help",
            background: "none",
            border: "none",
            padding: 0,
            font: "inherit",
            lineHeight: "inherit",
          }}
        >
          {children || phrase}
        </button>

        {open && definition && (
          <span
            id={tipId}
            role="tooltip"
            className="term-tooltip-panel term-tip-enter"
            style={{
              position: "absolute",
              zIndex: 60,
              left: 0,
              top: "calc(100% + 6px)",
              width: 280,
              maxWidth: "min(18rem, 90vw)",
              padding: "12px 14px",
              borderRadius: 12,
              pointerEvents: "none",
            }}
          >
            {/* term header */}
            <span
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                marginBottom: 6,
              }}
            >
              {/* mini sparkle */}
              <svg width="10" height="10" viewBox="0 0 24 24" fill="#004f73">
                <path d="M12 2l2.09 6.26L20 10l-5.91 1.74L12 18l-2.09-6.26L4 10l5.91-1.74z" />
              </svg>
              <span className="term-tooltip-heading">
                {phrase}
              </span>
            </span>

            {/* definition */}
            <span className="term-tooltip-body">
              {definition}
            </span>

            {onAskInGuide && (
              <span className="term-tooltip-hint">
                Click to ask the AI guide →
              </span>
            )}
          </span>
        )}
      </span>
    </>
  );
}
